import { h } from '../../../../src/shared/components/dom.js';
import { uploadUserRom } from '../api.js';
import { prepareUpload, acceptExtensions } from './upload-rom-model.js';
import { systemName } from '../shared/system-meta.js';

// Row copy per server outcome. `claimed` = byte-verified bundled match, no
// bytes stored — word it as a win, not a skip.
const OUTCOME_COPY = {
  claimed: 'Already in RetroPlay. Added to your ROMs',
  stored: 'Saved to your ROMs',
  deduplicated: 'Already in your ROMs',
  restored: 'Saved your copy. This game left the RetroPlay catalog',
};
const ERROR_COPY = {
  unsupported: 'Unsupported file type',
  no_rom_in_zip: 'No ROM found in the zip',
  too_large: 'Larger than the 32 MB upload limit',
  quota_exceeded: 'Your ROM storage is full. Remove uploads to free space',
  hash_collision: 'Couldn’t save: this file collides with one of your existing uploads',
  auth: 'Sign in to upload ROMs',
  network: 'Upload failed. Try again',
};

// Only cross-instance state: how to close the currently-open dialog so a
// new open replaces it cleanly (fires its onDone, unbinds its Escape).
let _activeClose = null;

/**
 * Open the upload dialog. `files` (optional) seeds rows from a drop;
 * `defaultSystem` pre-selects ambiguous-.bin selects when it's an option;
 * `onDone(successCount)` fires exactly once when the dialog closes. All
 * other state is closure-local to this open — a dialog closed mid-batch
 * stops uploading at the next file boundary and discards late results
 * (the in-flight request completes server-side; uploads are idempotent
 * via create-only dedup). Visibility uses the `hidden` class so the global
 * focus-trap manager owns Tab confinement + focus restore.
 */
export function openUploadRomModal({ files = null, defaultSystem = null, onDone = null } = {}) {
  if (_activeClose) _activeClose();
  document.querySelector('.lib-upload-modal')?.remove();

  // ── Per-open state (never module-level) ────────────────────────────
  const rows = [];        // { plan, el, status, systemSel, done }
  let successCount = 0;
  let closed = false;
  let doneFired = false;

  const input = h('input', {
    type: 'file',
    class: 'lib-upload-input',
    accept: acceptExtensions(),
    multiple: true,
    'aria-label': 'Choose ROM files to upload',
  });
  const rowsList = h('ul', { class: 'lib-upload-rows', role: 'status', 'aria-live': 'polite' });
  const actionBtn = h('button', { type: 'button', class: 'lib-btn lib-upload-go' }, 'Upload');
  const closeBtn = h('button', { type: 'button', class: 'lib-upload-close', 'aria-label': 'Close' }, '×');

  const dialog = h('div', {
    class: 'lib-upload-modal hidden',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-labelledby': 'lib-upload-title',
  },
    h('div', { class: 'lib-upload-panel' },
      h('div', { class: 'lib-upload-header' },
        h('h2', { id: 'lib-upload-title', class: 'lib-upload-title' }, 'Upload ROMs'),
        closeBtn,
      ),
      h('p', { class: 'lib-upload-hint' },
        'Pick ROM files you own (zips are unwrapped automatically). Games already in RetroPlay are recognized instantly and use no storage.'),
      input,
      rowsList,
      h('div', { class: 'lib-upload-actions' }, actionBtn),
    ),
  );

  function close() {
    if (doneFired) return;
    doneFired = true;
    closed = true;
    dialog.classList.add('hidden');
    document.removeEventListener('keydown', escHandler);
    if (_activeClose === close) _activeClose = null;
    if (onDone) onDone(successCount);
  }
  const escHandler = (e) => { if (e.key === 'Escape') close(); };
  closeBtn.addEventListener('click', close);

  function rowEl(plan) {
    const status = h('span', { class: 'lib-upload-row-status' },
      plan.error ? ERROR_COPY[plan.error] : 'Ready');
    const systemSel = plan.ambiguous
      ? h('select', { class: 'lib-upload-row-select', 'aria-label': `System for ${plan.name}` },
          ...plan.options.map(id => h('option', { value: id }, systemName(id))))
      : null;
    if (systemSel && defaultSystem && plan.options.includes(defaultSystem)) {
      systemSel.value = defaultSystem;
    }
    const el = h('li', { class: `lib-upload-row${plan.error ? ' lib-upload-row-error' : ''}` },
      h('span', { class: 'lib-upload-row-name', title: plan.name }, plan.name),
      systemSel,
      status,
    );
    return { plan, el, status, systemSel, done: false };
  }

  function syncActionButton() {
    const pending = rows.filter(r => !r.done && !r.plan.error);
    const errored = pending.filter(r => r.el.classList.contains('lib-upload-row-error'));
    if (pending.length === 0) {
      actionBtn.textContent = rows.length ? 'Done' : 'Upload';
      actionBtn.disabled = rows.length === 0;
      actionBtn.onclick = close;
      return;
    }
    actionBtn.textContent = errored.length === pending.length ? 'Retry failed' : 'Upload';
    actionBtn.disabled = false;
    actionBtn.onclick = runBatch;
  }

  async function addFiles(picked) {
    for (const file of picked) {
      const plan = await prepareUpload(file);
      if (closed) return; // dialog closed while a file was being read
      const row = rowEl(plan);
      rows.push(row);
      rowsList.appendChild(row.el);
    }
    syncActionButton();
  }

  async function runBatch() {
    actionBtn.disabled = true;
    const pending = rows.filter(r => !r.done && !r.plan.error);
    for (const row of pending) {
      if (closed) return;                     // dialog gone — stop cleanly
      const system = row.systemSel ? row.systemSel.value : row.plan.system;
      row.el.classList.remove('lib-upload-row-error');
      row.status.textContent = 'Uploading…';
      try {
        const out = await uploadUserRom(system, row.plan.bytes, row.plan.name);
        if (closed) return;                   // late result — instance is dead
        // upgraded (stored→claim) and restored (stale claim→stored again)
        // are CATALOG MUTATIONS despite deduplicating — the tile's state
        // just changed, so they show transition copy and count toward
        // successCount → onDone reloads the stale tile.
        const key = out.restored ? 'restored'
          : out.upgraded ? 'claimed'
          : out.deduplicated ? 'deduplicated'
          : out.claimed ? 'claimed' : 'stored';
        row.status.textContent = OUTCOME_COPY[key];
        row.el.classList.add('lib-upload-row-ok');
        row.done = true;
        if (row.systemSel) row.systemSel.disabled = true;
        if (!out.deduplicated || out.upgraded || out.restored) successCount += 1;
      } catch (err) {
        if (closed) return;
        const key = err?.status === 401 ? 'auth'
          : err?.body?.error === 'quota_exceeded' ? 'quota_exceeded'
          : err?.body?.error === 'hash_collision' ? 'hash_collision'
          : 'network';
        row.status.textContent = ERROR_COPY[key];
        row.el.classList.add('lib-upload-row-error');
        // A collision can never succeed on retry — make the row terminal so
        // "Retry failed" doesn't loop on it (plan.error excludes it from
        // the pending set, like a pre-flight rejection).
        if (key === 'hash_collision') row.plan.error = 'hash_collision';
      }
    }
    syncActionButton();
  }

  input.addEventListener('change', () => addFiles([...input.files]));
  syncActionButton();

  document.body.appendChild(dialog);
  dialog.classList.remove('hidden');
  document.addEventListener('keydown', escHandler);
  _activeClose = close;
  // The global focus manager (focus-trap.js) confines Tab and restores
  // focus on close, but deliberately does not PLACE initial focus — every
  // library dialog self-focuses (detail-panel, add-to-list). The file
  // input is this dialog's primary task, so it gets focus even when rows
  // were seeded by a drop (adding more files stays one keystroke away).
  // Deferred one animation frame (mirrors detail-panel.js) so the trap's
  // MutationObserver microtask reads document.activeElement — the true
  // opener — BEFORE this self-focus lands; focusing synchronously here
  // would make the trap see the file input itself as the opener and skip
  // focus restore entirely on close.
  requestAnimationFrame(() => input.focus({ preventScroll: true }));
  if (files && files.length) addFiles([...files]);
}
